import { CheckCircle2, PauseCircle, PlayCircle, Plus, ShieldAlert, ShieldCheck, Target, Unlock } from "lucide-react";
import type { Account, SavingGoal } from "../../types";
import { money } from "../../lib/finance";
import { goalCurrentSaved, goalMonthStatus, goalPacing } from "../../lib/goals";
import { goalCushionStatus } from "../../lib/goal-reserves";
import { Private } from "../../components/Privacy";

export interface GoalCardProps {
  goal: SavingGoal;
  today: string;
  month: string;
  accounts?: Account[];
  onOpen: (goal: SavingGoal) => void;
  onTopUp: (goal: SavingGoal) => void;
  onSkipMonth: (goal: SavingGoal, month: string) => void;
  onResumeMonth: (goal: SavingGoal, month: string) => void;
  onUnfloat?: (goal: SavingGoal) => void;
}

export default function GoalCard({
  goal,
  today,
  month,
  accounts,
  onOpen,
  onTopUp,
  onSkipMonth,
  onResumeMonth,
  onUnfloat,
}: GoalCardProps) {
  const pacing = goalPacing(goal, today);
  const currentSaved = goalCurrentSaved(goal, month);
  const mStatus = goalMonthStatus(goal, month, today.slice(0, 7));
  const cushionStatus = goal.completedDate ? null : goalCushionStatus(goal, accounts);

  return (
    <div className="goal-card" onClick={() => onOpen(goal)} role="button" tabIndex={0}>
      <div className="goal-card-top">
        <div className="goal-card-identity">
          <span className="goal-card-icon">
            <Target size={18} />
          </span>
          <div className="goal-card-title-wrap">
            <h3>{goal.name}</h3>
            <span className="goal-card-subtitle">
              {goal.profile ? goal.profile : "Household"}
              {pacing.projectedCompletionMonth && (
                <> · Est. {pacing.projectedCompletionMonth}</>
              )}
              {cushionStatus && (
                <> · Cushion: <Private>{money(cushionStatus.cushionAmount, goal.currency)}</Private></>
              )}
            </span>
          </div>
        </div>
        <div className="goal-card-badges">
          {goal.floatingGroup && (
            <span className="goal-badge floating">Floating</span>
          )}
          {cushionStatus && (currentSaved >= goal.targetAmount || goal.status === "achieved") && (
            cushionStatus.isSafeToSpend ? (
              <span className="goal-badge achieved" title={`Account balance preserves ${money(cushionStatus.cushionAmount, goal.currency)} cushion`}>
                <ShieldCheck size={11} style={{ verticalAlign: "-1px", marginRight: 3 }} /> Safe to buy
              </span>
            ) : (
              <span className="goal-badge paused" title={`Needs ${money(cushionStatus.shortfall, goal.currency)} more to preserve cushion`}>
                <ShieldAlert size={11} style={{ verticalAlign: "-1px", marginRight: 3 }} /> Needs cushion
              </span>
            )
          )}
          <span className={`goal-badge ${goal.status}`}>
            {goal.status}
          </span>
        </div>
      </div>

      <div className="goal-progress-wrap">
        <div className="goal-figures">
          <strong>
            <Private>{money(currentSaved, goal.currency)}</Private>
          </strong>
          <span className="goal-target-text">
            of <Private>{money(goal.targetAmount, goal.currency)}</Private> ({pacing.percentComplete}%)
          </span>
        </div>
        <div className="goal-progress-bar">
          <div
            className="goal-progress-fill"
            style={{ width: `${pacing.percentComplete}%` }}
          />
        </div>
      </div>

      {cushionStatus && currentSaved >= goal.targetAmount && (
        cushionStatus.isSafeToSpend ? (
          <div className="goal-cushion-notice" onClick={(e) => e.stopPropagation()}>
            <ShieldCheck size={14} style={{ flexShrink: 0, marginTop: 1 }} />
            <span>
              Ready to buy! Account retains <Private>{money(cushionStatus.postPurchaseBalance ?? 0, goal.currency)}</Private>, safely above your <Private>{money(cushionStatus.cushionAmount, goal.currency)}</Private> buffer.
            </span>
          </div>
        ) : (
          <div className="goal-cushion-alert" onClick={(e) => e.stopPropagation()}>
            <ShieldAlert size={14} style={{ flexShrink: 0, marginTop: 1 }} />
            <span>
              Target reached, but buying now would dip into your buffer. Save <Private>{money(cushionStatus.shortfall, goal.currency)}</Private> more in {cushionStatus.accountName || "account"} to keep your <Private>{money(cushionStatus.cushionAmount, goal.currency)}</Private> cushion.
            </span>
          </div>
        )
      )}

      {mStatus.scheduledAmount > 0 && !goal.completedDate && (
        <div
          className={`goal-card-status-row ${mStatus.isSkipped ? "is-skipped" : ""}`}
          onClick={(e) => e.stopPropagation()}
        >
          {mStatus.isSkipped ? (
            <>
              <span>{month}: Skipped</span>
              <button
                type="button"
                className="small"
                onClick={() => onResumeMonth(goal, month)}
              >
                <PlayCircle size={13} /> Resume accrual
              </button>
            </>
          ) : mStatus.contributedAmount >= mStatus.scheduledAmount ? (
            <>
              <span>
                <CheckCircle2 size={13} style={{ verticalAlign: "middle", marginRight: 4, color: "var(--mint)" }} />
                {month}: +{money(mStatus.contributedAmount, goal.currency)} accrued
              </span>
              <button
                type="button"
                className="small"
                onClick={() => onSkipMonth(goal, month)}
              >
                <PauseCircle size={13} /> Skip
              </button>
            </>
          ) : (
            <>
              <span>
                {month}: {money(mStatus.contributedAmount, goal.currency)} of {money(mStatus.scheduledAmount, goal.currency)}
              </span>
              <div style={{ display: "flex", gap: "0.25rem" }}>
                <button
                  type="button"
                  className="small"
                  onClick={() => onSkipMonth(goal, month)}
                >
                  Skip
                </button>
                <button
                  type="button"
                  className="primary small"
                  onClick={() => onResumeMonth(goal, month)}
                >
                  Contribute
                </button>
              </div>
            </>
          )}
        </div>
      )}

      <div className="goal-card-actions" onClick={(e) => e.stopPropagation()}>
        {!goal.completedDate && <button
          type="button"
          className="primary small"
          onClick={() => onTopUp(goal)}
        >
          <Plus size={13} /> Top up
        </button>}
        {goal.completedDate && <span className="goal-detail-sub">Saving complete · Funds released</span>}
        {goal.floatingGroup && onUnfloat && !goal.completedDate && (
          <button
            type="button"
            className="small"
            onClick={() => onUnfloat(goal)}
          >
            <Unlock size={13} /> Unfloat
          </button>
        )}
      </div>
    </div>
  );
}
